import React, { useState } from 'react';
import { WBox, Dot, BG } from '../../../homepage/components/ProfessionalHomepage/DiagramUtils';

/* ── Inline ownership org chart ─────────────────────────────── */
function OwnershipOrgChart() {
  return (
    <svg
      viewBox="0 0 860 240"
      className="investors-org-chart-svg"
      role="img"
      aria-label="Besper Holding GmbH owns 60% of Besper UG Platform, 100% of Infra, 100% of Labs, and 80% of Ontology."
    >
      <BG id="org-bg" w={860} h={240} mode="light" />
      {/* Holding (top center) */}
      <WBox x={330} y={10} w={200} h={52} label="Besper Holding GmbH" sub="Group Holding" accent={true} mode="light" />

      {/* Branch lines */}
      <line x1="430" y1="62" x2="430" y2="108" stroke="rgba(0,48,87,0.38)" strokeWidth="1" />
      <line x1="115" y1="108" x2="745" y2="108" stroke="rgba(0,48,87,0.38)" strokeWidth="1" />
      <line x1="115" y1="108" x2="115" y2="158" stroke="rgba(0,48,87,0.38)" strokeWidth="1" />
      <line x1="310" y1="108" x2="310" y2="158" stroke="rgba(0,48,87,0.38)" strokeWidth="1" />
      <line x1="550" y1="108" x2="550" y2="158" stroke="rgba(0,48,87,0.38)" strokeWidth="1" />
      <line x1="745" y1="108" x2="745" y2="158" stroke="rgba(0,48,87,0.38)" strokeWidth="1" />

      {/* Ownership % labels */}
      <text x="115" y="103" textAnchor="middle" fill="rgba(0,80,180,0.75)" fontSize="9" fontFamily="'DM Mono','Courier New',monospace">60%</text>
      <text x="310" y="103" textAnchor="middle" fill="rgba(0,80,180,0.75)" fontSize="9" fontFamily="'DM Mono','Courier New',monospace">100%</text>
      <text x="550" y="103" textAnchor="middle" fill="rgba(0,80,180,0.75)" fontSize="9" fontFamily="'DM Mono','Courier New',monospace">100%</text>
      <text x="745" y="103" textAnchor="middle" fill="rgba(0,80,180,0.75)" fontSize="9" fontFamily="'DM Mono','Courier New',monospace">80%</text>

      {/* Junction dots */}
      <Dot cx={115} cy={108} mode="light" />
      <Dot cx={310} cy={108} mode="light" />
      <Dot cx={550} cy={108} mode="light" />
      <Dot cx={745} cy={108} mode="light" />
      <Dot cx={430} cy={108} mode="light" accent={true} />

      {/* 4 subsidiary boxes */}
      <WBox x={30} y={158} w={170} h={52} label="Besper UG" sub="Open Source Platform" mode="light" accent={true} />
      <WBox x={225} y={158} w={170} h={52} label="B-esper Infra" sub="Hard Infra & Compute" mode="light" />
      <WBox x={465} y={158} w={170} h={52} label="B-esper Labs" sub="Innovation Hub" mode="light" />
      <WBox x={660} y={158} w={170} h={52} label="B-esper Ontology" sub="Analytics & Ops Hub" mode="light" />
    </svg>
  );
}

/**
 * Section 02: legal ownership vs operational (marketplace) model with tabs.
 */
export function InvestorsCompanyStructureSection({ t }) {
  const [modelTab, setModelTab] = useState('ownership');

  const subtitle =
    modelTab === 'ownership'
      ? t('platformStructure.introOwnership', 'Legal structure: how Besper Holding GmbH holds stakes in the operating companies.')
      : t(
          'platformStructure.introOperational',
          'Group companies use the same marketplace as third parties: some sell listings, some buy capacity.',
        );

  return (
    <section className="investors-section investors-section-structure">
      <span className="investors-section-overline" aria-hidden="true">02</span>
      <div className="investors-bleed-inner">
        <h2 className="investors-section-title">
          {t('platformStructure.title', 'Group structure & operating model')}
        </h2>
        <p className="investors-section-subtitle investors-structure-section-dek">{subtitle}</p>

        <div
          className="investors-structure-model-tabs"
          role="tablist"
          aria-label={t('platformStructure.modelTablistLabel', 'Structure view')}
        >
          <button
            type="button"
            role="tab"
            aria-selected={modelTab === 'ownership'}
            id="investors-structure-tab-ownership"
            aria-controls="investors-structure-model-panel"
            className={`investors-structure-model-tab ${modelTab === 'ownership' ? 'investors-structure-model-tab-active' : ''}`}
            onClick={() => setModelTab('ownership')}
          >
            {t('platformStructure.tabOwnership', 'Ownership')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={modelTab === 'operational'}
            id="investors-structure-tab-operational"
            aria-controls="investors-structure-model-panel"
            className={`investors-structure-model-tab ${modelTab === 'operational' ? 'investors-structure-model-tab-active' : ''}`}
            onClick={() => setModelTab('operational')}
          >
            {t('platformStructure.tabOperational', 'Operational model')}
          </button>
        </div>

        <div
          id="investors-structure-model-panel"
          role="tabpanel"
          aria-labelledby={modelTab === 'ownership' ? 'investors-structure-tab-ownership' : 'investors-structure-tab-operational'}
          className="investors-structure-model-panel"
        >
          <div className="investors-structure-diagram" aria-hidden="true">
            {modelTab === 'ownership' ? (
              <OwnershipOrgChart />
            ) : (
              <p className="investors-structure-operational-note">
                {t(
                  'platformStructure.operationalSummary',
                  'All group companies use the same marketplace rails as third parties — Infra and Ontology sell listings, Labs buys compute capacity, and settlements flow through the platform marketplace.'
                )}
              </p>
            )}
          </div>
          {modelTab === 'ownership' ? (
            <p className="investors-structure-diagram-caption">
              {t(
                'platformStructure.ownershipCaption',
                'Percentages show Besper Holding GmbH’s stake in each entity; Besper UG operates the B-esper Platform product.'
              )}
            </p>
          ) : (
            <p className="investors-structure-diagram-caption">
              {t(
                'platformStructure.operationalCaption',
                'Same platform rails for everyone: Infra and Ontology sell, Labs buys, settlements go through the marketplace.',
              )}
            </p>
          )}
        </div>

        <div className="investors-structure-cards">
          <article className="investors-structure-card investors-structure-card-platform">
            <h3>{t('platformStructure.cardUgTitle', 'Besper UG — B-esper Platform')}</h3>
            <p>
              {modelTab === 'ownership'
                ? t(
                    'platformStructure.cardUgBodyOwnership',
                    'Manages the B-esper open-source platform and marketplace — orchestrator, billing engine, and the customer-facing product.'
                  )
                : t(
                    'platformStructure.cardUgBodyOperational',
                    'Managed hosting, identity, payments, and marketplace—Besper UG runs the hub; money flows through listings.',
                  )}
            </p>
          </article>
          <article className="investors-structure-card investors-structure-card-infra">
            <h3>{t('platformStructure.cardInfraTitle', 'B-esper Infra')}</h3>
            <p>
              {modelTab === 'ownership'
                ? t(
                    'platformStructure.cardInfraBodyOwnership',
                    'Provides hard infrastructure and compute — cluster operations, capacity provisioning, and hosting for the platform and workloads.'
                  )
                : t(
                    'platformStructure.cardInfraBodyOperational',
                    'Marketplace provider: compute and capacity sold via listings to Labs, Ontology, and third parties.',
                  )}
            </p>
          </article>
          <article className="investors-structure-card investors-structure-card-labs">
            <h3>{t('platformStructure.cardLabsTitle', 'B-esper Labs')}</h3>
            <p>
              {modelTab === 'ownership'
                ? t(
                    'platformStructure.cardLabsBodyOwnership',
                    'Our innovation hub — trustworthy AI research and experimental methods that drive the B-esper platform roadmap.'
                  )
                : t(
                    'platformStructure.cardLabsBodyOperational',
                    'Buys infrastructure and compute on the marketplace like any other customer.',
                  )}
            </p>
          </article>
          <article className="investors-structure-card investors-structure-card-ontology">
            <h3>{t('platformStructure.cardOntologyTitle', 'B-esper Ontology')}</h3>
            <p>
              {modelTab === 'ownership'
                ? t(
                    'platformStructure.cardOntologyBodyOwnership',
                    'The Analytics and Operational Hub for the B-esper Platform — deep analytics, decision-grade insights, and semantic alignment across the group.'
                  )
                : t(
                    'platformStructure.cardOntologyBodyOperational',
                    'Sells software and analytics on the marketplace; buys extra compute from providers such as Infra when needed.',
                  )}
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}
